import { computed } from 'vue';
import { useRigControlStore } from '@/stores/RigControlStore';
import type { BandType, CommonModes } from '@/types';
import isElectron from 'is-electron';

export function useRigControl() {
  const isDev = process.env.NODE_ENV === 'development';
  const rigControlStore = useRigControlStore();

  const isRigControlCapable = computed(() => isDev || isElectron());
  const isRigControlEnabled = computed(() => rigControlStore.enabled);
  const isRigControlAvailable = computed(() => isRigControlCapable.value && rigControlStore.rigControlAvailable);
  const isRigControlReconnecting = computed(() => rigControlStore.isReconnecting);
  const isRigControlConnecting = computed(() => rigControlStore.connecting);

  // Display name of the active backend, for tooltips and status copy
  const rigControlBackendLabel = computed(() =>
    rigControlStore.backend === 'hamlib' ? 'Hamlib (beta)' : 'Flrig'
  );

  const testConnection = () => rigControlStore.testConnection();

  const toggleRigControl = () => rigControlStore.toggleRigControl();

  const setFrequency = (frequency: number) => rigControlStore.setFrequency(frequency);
  const frequency = computed(() => rigControlStore.frequency);

  const setMode = (mode: CommonModes, band: BandType) => {
    return rigControlStore.setMode(mode, band);
  };

  // Set mode + frequency from a spot, then immediately read the rig back so the
  // status display updates without waiting for the next poll. Use this for spot
  // selection instead of separate setMode/setFrequency calls.
  const tuneToSpot = (frequencyHz: number, mode: CommonModes, band: BandType) => {
    return rigControlStore.tuneToSpot(frequencyHz, mode, band);
  };


  return {
    isRigControlAvailable,
    isRigControlEnabled,
    isRigControlCapable,
    isRigControlReconnecting,
    isRigControlConnecting,
    rigControlBackendLabel,
    setFrequency,
    frequency,
    setMode,
    tuneToSpot,
    testConnection,
    toggleRigControl,
  };
}